import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell } from 'lucide-react';
import { Skeleton } from '@solid/ui';
import { activityApi, type EntityType, type GroupedActivity } from '@/lib/api';
import { ACTIVITY_DISPLAY, activityGroupLabel, activityLink } from '@/lib/activity-display';
import { Card } from '@/components/ui/Card';
import { cn, formatRelativeTime } from '@/lib/utils';

const PAGE = 6;

/**
 * Your Activity for this one project, on its Overview (Jesse, 2026-10-07):
 * Activity is an agency screen in the nav, so this card is how a client sees
 * mentions, replies, assignments and forms meant for them. Newest first, read
 * and unread alike; opening an item marks it read. Like the Overview's other
 * cards it hides itself when there's nothing to show.
 *
 * The query key starts with 'activity', which the socket store invalidates on
 * every new item, so the card updates live.
 */
export function ProjectActivityCard({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [limit, setLimit] = useState(PAGE);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['activity', 'project', projectId, limit],
    queryFn: () => activityApi.list({ projectId, grouped: true, limit }),
    // Show more keeps the list on screen while the longer page loads
    placeholderData: keepPreviousData,
  });

  const markRead = useMutation({
    mutationFn: (a: GroupedActivity) =>
      a.count > 1
        ? activityApi.markGroupAsRead({
            entityType: a.entityType as EntityType | undefined,
            entityId: a.entityId,
            activityType: a.type,
          })
        : activityApi.markAsRead(a.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity'] });
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activity-unread-count'] });
    },
  });

  if (isLoading) return <Skeleton className="h-48 rounded-xl" />;

  const header = (
    <div className="mb-3 flex items-center gap-2">
      <Bell className="h-5 w-5 text-accent" aria-hidden />
      <h2 className="text-lg font-semibold text-secondary">Activity</h2>
      {!!data?.unreadCount && <span className="text-sm text-grey-400">({data.unreadCount} new)</span>}
    </div>
  );

  if (isError || !data) {
    return (
      <Card>
        {header}
        <p className="flex items-center gap-1.5 text-sm font-semibold text-error">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          Couldn't load
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-1.5 text-sm font-semibold text-accent-700 underline-offset-2 hover:underline"
        >
          Retry
        </button>
      </Card>
    );
  }

  if (data.items.length === 0) return null;

  return (
    <Card>
      {header}
      <ul className="-mx-2 divide-y divide-grey-100">
        {data.items.map((a) => {
          const display = ACTIVITY_DISPLAY[a.type];
          const Icon = display?.icon ?? Bell;
          const unread = a.hasUnread || !a.isRead;
          return (
            <li key={`${a.entityType}-${a.entityId}-${a.type}`}>
              <Link
                to={activityLink(a)}
                onClick={() => unread && markRead.mutate(a)}
                className="flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-grey-50"
              >
                <span
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    display?.bgColor ?? 'bg-grey-100',
                  )}
                >
                  <Icon className={cn('h-4 w-4', display?.color ?? 'text-grey-500')} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('line-clamp-2 text-sm text-secondary', unread ? 'font-semibold' : 'font-medium')}>
                    {a.count > 1 ? activityGroupLabel(a.type, a.count) : a.title}
                  </span>
                  {a.description && (
                    <span className="line-clamp-1 text-xs text-grey-500">
                      {a.count > 1 ? `in “${a.description}”` : a.description}
                    </span>
                  )}
                  <span className="text-xs text-grey-400">{formatRelativeTime(new Date(a.createdAt))}</span>
                </span>
                {unread && (
                  <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary">
                    <span className="sr-only">Unread</span>
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
      {data.pagination.hasMore && (
        <button
          type="button"
          onClick={() => setLimit((n) => n + PAGE)}
          className="mt-3 text-sm text-accent transition-colors hover:text-accent/80"
        >
          Show more
        </button>
      )}
    </Card>
  );
}
